"use client";
import { useState, useEffect, Suspense } from "react";
import { OtherGames } from "@/components/ui/games/OtherGames";
import { useSearchParams } from "next/navigation";
import { downloadCertificate, getDayOfWeek, formatTime } from "@/components/ui/games/GameCertificate";
import { Difficulty, GameState, PipsPuzzle } from "./constants";
import { getTodaysPuzzle } from "./pips_puzzles";
import Domino from "./Domino";
import GameBoard from "./GameBoard";

function PipsPageContent() {
  const searchParams = useSearchParams();
  const isClubhouse = searchParams?.get("id") === "clubhouse";

  // Game states
  const [playerName, setPlayerName] = useState<string>("");
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [difficulty, setDifficulty] = useState<Difficulty>("easy");
  const [gameState, setGameState] = useState<GameState>("playing");
  const [startTime, setStartTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [currentPuzzle, setCurrentPuzzle] = useState<PipsPuzzle | null>(null);
  const [selectedDominoIndex, setSelectedDominoIndex] = useState<number | null>(null);
  const [dominoRotations, setDominoRotations] = useState<Record<number, number>>({});
  const [placedDominoes, setPlacedDominoes] = useState<
    Array<{
      dominoIndex: number;
      cells: Array<{ row: number; col: number; pipValue: number }>;
    }>
  >([]);
  const [showHowToPlay, setShowHowToPlay] = useState<boolean>(false);

  // Timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (startTime && gameState === "playing") {
      interval = setInterval(() => {
        setCurrentTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, gameState]);

  // Check win condition only when all dominoes are placed
  useEffect(() => {
    if (!currentPuzzle || gameState !== "playing") return;

    console.log("Checking win condition...", {
      placedCount: placedDominoes.length,
      totalDominoes: currentPuzzle.dominoes.length,
    });

    // Only check if all dominoes are placed
    if (placedDominoes.length !== currentPuzzle.dominoes.length) {
      console.log("Not all dominoes placed yet");
      return;
    }

    const won = checkWinCondition();
    console.log("Win condition result:", won);

    if (won) {
      setGameState("won");
    }
  }, [placedDominoes, gameState, currentPuzzle]);

  // Handlers
  const handleStartGame = () => {
    if (playerName.trim().length < 2) {
      setErrorMessage("Please enter your name (at least 2 characters)");
      return;
    }

    // Load the puzzle for the selected difficulty
    const puzzle = getTodaysPuzzle(difficulty);
    setCurrentPuzzle(puzzle);

    setGameStarted(true);
    setStartTime(Date.now());
    setErrorMessage("");
  };

  // Helpers
  const checkWinCondition = () => {
    if (!currentPuzzle) return false;

    // Check if all dominoes are placed
    if (placedDominoes.length !== currentPuzzle.dominoes.length) return false;

    // Count how many regions have their conditions satisfied
    let totalRegionsWithConditions = 0;
    let satisfiedRegions = 0;

    console.log("=== Checking regions ===");

    // Check each region's condition
    for (const [regionId, region] of Object.entries(currentPuzzle.regions)) {
      if (!region.condition) continue;

      totalRegionsWithConditions++;

      // Collect pip values for this region from placed dominoes
      const regionCells: number[] = [];
      let totalRegionCells = 0;

      // Count total cells and gather pip values for this region
      currentPuzzle.board.layout.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell === regionId) {
            totalRegionCells++;
          }
        });
      });

      // Gather pip values from placed dominoes that are in this region
      placedDominoes.forEach((domino) => {
        domino.cells.forEach((cell) => {
          if (currentPuzzle.board.layout[cell.row]?.[cell.col] === regionId) {
            regionCells.push(cell.pipValue);
          }
        });
      });

      // Validate condition
      const { type, value } = region.condition;
      let passed = false;

      switch (type) {
        case "sum":
          const sum = regionCells.reduce((a, b) => a + b, 0);
          passed = value !== undefined && sum === value;
          console.log(`Region ${regionId}: sum=${sum}, expected=${value}, passed=${passed}`);
          break;
        case "equal":
          passed = regionCells.length > 0 && regionCells.every((v) => v === regionCells[0]);
          console.log(`Region ${regionId}: equal, values=${regionCells}, passed=${passed}`);
          break;
        case "not-equal":
          passed = regionCells.length === 0 || !regionCells.some((v, i) => regionCells.indexOf(v) !== i);
          console.log(`Region ${regionId}: not-equal, values=${regionCells}, passed=${passed}`);
          break;
        case "less-than":
          const sumLT = regionCells.reduce((a, b) => a + b, 0);
          passed = value !== undefined && sumLT < value;
          console.log(`Region ${regionId}: less-than, sum=${sumLT}, expected<${value}, passed=${passed}`);
          break;
        case "greater-than":
          const sumGT = regionCells.reduce((a, b) => a + b, 0);
          passed = value !== undefined && sumGT > value;
          console.log(`Region ${regionId}: greater-than, sum=${sumGT}, expected>${value}, passed=${passed}`);
          break;
      }

      if (passed) {
        satisfiedRegions++;
      }
    }

    console.log(`Satisfied: ${satisfiedRegions}/${totalRegionsWithConditions}`);
    return satisfiedRegions === totalRegionsWithConditions && totalRegionsWithConditions > 0;
  };

  const calculateCells = (row: number, col: number, rotation: number, pip1: number, pip2: number) => {
    const normalizedRotation = rotation % 360;
    const isVertical = normalizedRotation === 90 || normalizedRotation === 270;
    const isFlipped = normalizedRotation === 180 || normalizedRotation === 270;

    const [firstPip, secondPip] = isFlipped ? [pip2, pip1] : [pip1, pip2];

    return isVertical
      ? [
          { row, col, pipValue: firstPip },
          { row: row + 1, col, pipValue: secondPip },
        ]
      : [
          { row, col, pipValue: firstPip },
          { row, col: col + 1, pipValue: secondPip },
        ];
  };

  const isValidPlacement = (
    cells: Array<{ row: number; col: number; pipValue: number }>,
    excludeDominoIndex?: number
  ) => {
    if (!currentPuzzle) return false;

    const { board } = currentPuzzle;

    // Check bounds
    if (cells.some((cell) => cell.row < 0 || cell.row >= board.height || cell.col < 0 || cell.col >= board.width)) {
      return false;
    }

    // Check valid board spaces
    if (
      cells.some((cell) => {
        const cellValue = board.layout[cell.row]?.[cell.col];
        return cellValue === "" || cellValue === undefined;
      })
    ) {
      return false;
    }

    // Check overlaps with other dominoes
    const occupiedCells = new Set<string>();
    placedDominoes.forEach((d) => {
      if (excludeDominoIndex === undefined || d.dominoIndex !== excludeDominoIndex) {
        d.cells.forEach((cell) => occupiedCells.add(`${cell.row},${cell.col}`));
      }
    });

    return !cells.some((cell) => occupiedCells.has(`${cell.row},${cell.col}`));
  };

  const autoRotateTrayDominoToHorizontal = (index: number) => {
    const isPlaced = placedDominoes.some((d) => d.dominoIndex === index);
    if (isPlaced) return;

    const rotation = dominoRotations[index] || 0;
    const normalizedRotation = rotation % 360;

    // If vertical (90° or 270°), rotate to next horizontal (always forward)
    if (normalizedRotation === 90 || normalizedRotation === 270) {
      setDominoRotations((prev) => ({ ...prev, [index]: rotation + 90 }));
    }
  };

  const handleDominoClick = (index: number, clickedRow?: number, clickedCol?: number, e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (selectedDominoIndex === index) {
      // Rotate the selected domino by 90 degrees (always forward)
      const currentRotation = dominoRotations[index] || 0;
      const newRotation = currentRotation + 90;

      // Check if this domino is placed on the board
      const isPlaced = placedDominoes.some((d) => d.dominoIndex === index);

      if (isPlaced && currentPuzzle) {
        // Board domino - validate rotation before applying
        const [pip1, pip2] = currentPuzzle.dominoes[index];
        const placed = placedDominoes.find((d) => d.dominoIndex === index);
        if (!placed) return;

        const anchorCell = placed.cells[0];
        const newCells = calculateCells(anchorCell.row, anchorCell.col, newRotation, pip1, pip2);

        // Only rotate if the new position is valid
        if (!isValidPlacement(newCells, index)) return;

        setDominoRotations((prev) => ({ ...prev, [index]: newRotation }));
        setPlacedDominoes((prev) => prev.map((d) => (d.dominoIndex === index ? { ...d, cells: newCells } : d)));
      } else {
        // Tray domino - rotate freely
        setDominoRotations((prev) => ({ ...prev, [index]: newRotation }));
      }
    } else {
      // Auto-rotate previous selection if needed
      if (selectedDominoIndex !== null) {
        autoRotateTrayDominoToHorizontal(selectedDominoIndex);
      }

      // Select the domino
      setSelectedDominoIndex(index);
    }
  };

  const handleBackgroundClick = () => {
    setSelectedDominoIndex(null);
  };

  const handleAvailableTrayClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // If a placed domino is selected, remove it from the board
    if (selectedDominoIndex !== null) {
      const isPlaced = placedDominoes.some((d) => d.dominoIndex === selectedDominoIndex);
      if (isPlaced) {
        setPlacedDominoes((prev) => prev.filter((d) => d.dominoIndex !== selectedDominoIndex));
        // Rotate to next horizontal position (always forward to avoid backwards animation)
        const currentRotation = dominoRotations[selectedDominoIndex] || 0;
        const normalizedRotation = currentRotation % 360;
        if (normalizedRotation === 90 || normalizedRotation === 270) {
          setDominoRotations((prev) => ({ ...prev, [selectedDominoIndex]: currentRotation + 90 }));
        }
      }
    }
    setSelectedDominoIndex(null);
  };

  const handleBoardCellClick = (row: number, col: number) => {
    if (selectedDominoIndex === null || !currentPuzzle) return;

    const [pip1, pip2] = currentPuzzle.dominoes[selectedDominoIndex];
    const rotation = dominoRotations[selectedDominoIndex] || 0;
    const cells = calculateCells(row, col, rotation, pip1, pip2);

    if (!isValidPlacement(cells, selectedDominoIndex)) {
      setSelectedDominoIndex(null);
      return;
    }

    setPlacedDominoes((prev) => [
      ...prev.filter((d) => d.dominoIndex !== selectedDominoIndex),
      { dominoIndex: selectedDominoIndex, cells },
    ]);
    setSelectedDominoIndex(null);
  };

  // Start screen
  if (!gameStarted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        {isClubhouse && <img src={"/landing/clubhouse.png"} alt="Clubhouse" className="h-20 w-auto mb-8" />}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">Pips</h1>
          <p className="text-lg text-gray-600 mb-2">Arrange dominoes to solve logic puzzles</p>
          <p className="text-base text-gray-500">{getDayOfWeek()}</p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="space-y-6">
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full px-6 py-4 text-base border-2 border-indigo-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              maxLength={20}
              autoFocus
            />

            {errorMessage && <div className="text-red-600 text-center font-medium">{errorMessage}</div>}

            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Select Difficulty</p>
              <div className="grid grid-cols-3 gap-2">
                {(["easy", "medium", "hard"] as const).map((level) => {
                  const isActive = difficulty === level;
                  const colors = {
                    easy: isActive
                      ? "bg-green-100 text-green-700 ring-2 ring-green-300"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100",
                    medium: isActive
                      ? "bg-yellow-100 text-yellow-700 ring-2 ring-yellow-300"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100",
                    hard: isActive
                      ? "bg-red-100 text-red-700 ring-2 ring-red-300"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100",
                  };
                  return (
                    <button
                      key={level}
                      onClick={() => setDifficulty(level)}
                      className={`py-3 px-4 rounded-xl font-medium transition-all ${colors[level]}`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                {difficulty === "easy" && "4-5 dominoes, simple conditions"}
                {difficulty === "medium" && "7-9 dominoes, moderate challenge"}
                {difficulty === "hard" && "10-16 dominoes, complex puzzle"}
              </p>
            </div>

            <button
              onClick={handleStartGame}
              className="w-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 text-base rounded-xl font-bold transition-all transform hover:scale-105 shadow-sm"
            >
              Start Playing →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Game screen
  return (
    <div className="min-h-screen px-4 py-4 pt-20" onClick={handleBackgroundClick}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-8 gap-3 sm:gap-0">
          <div className="flex items-center gap-2 sm:gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-black">Pips</h1>
            <span className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-gray-100 text-gray-700">
              {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowHowToPlay(true);
              }}
              className="px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
            >
              How to Play
            </button>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="text-base sm:text-lg font-mono text-gray-600">{formatTime(currentTime)}</div>
            <div className="text-base sm:text-lg font-medium text-gray-700">{playerName}</div>
          </div>
        </div>

        {/* Game Board and Dominoes */}
        {currentPuzzle ? (
          (() => {
            // Calculate domino size based on grid dimensions and viewport
            const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
            const maxContainerSize = isMobile ? Math.min(window.innerWidth - 32, 400) : 512;
            const cellSize = Math.floor(
              maxContainerSize / Math.max(currentPuzzle.board.width, currentPuzzle.board.height)
            );
            const dominoSize = Math.max(24, Math.min(cellSize * 0.8, 64));

            return (
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
                {/* Board */}
                <div className="flex-1">
                  <div className="rounded-lg p-2 sm:p-4 lg:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-4 text-center">
                      Game Board
                    </h3>
                    <div className="flex justify-center">
                      <GameBoard
                        puzzle={currentPuzzle}
                        onCellClick={handleBoardCellClick}
                        onDominoClick={handleDominoClick}
                        placedDominoes={placedDominoes}
                        dominoRotations={dominoRotations}
                        selectedDominoIndex={selectedDominoIndex}
                      />
                    </div>
                  </div>
                </div>

                {/* Available Dominoes */}
                <div className="w-full lg:w-80">
                  <div
                    className="rounded-lg p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer"
                    onClick={handleAvailableTrayClick}
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">Available Dominoes</h3>
                    <div className="flex flex-wrap gap-1 sm:gap-2 justify-center domino-container mb-3 sm:mb-4">
                      {currentPuzzle.dominoes.map((domino, index) => {
                        const isPlaced = placedDominoes.some((d) => d.dominoIndex === index);
                        if (isPlaced) return null;

                        return (
                          <Domino
                            key={index}
                            pips={domino}
                            rotation={dominoRotations[index] || 0}
                            isSelected={selectedDominoIndex === index}
                            onClick={(e) => handleDominoClick(index, undefined, undefined, e)}
                            size={dominoSize}
                          />
                        );
                      })}
                    </div>
                    {selectedDominoIndex !== null && (
                      <div className="mt-4 text-center text-sm text-gray-600">
                        <p>Selected: [{currentPuzzle.dominoes[selectedDominoIndex].join("-")}]</p>
                        <p className="text-xs mt-1">
                          {placedDominoes.some((d) => d.dominoIndex === selectedDominoIndex)
                            ? "Click to return"
                            : "Click to rotate"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500">Loading puzzle...</p>
          </div>
        )}

        <div className="mb-8"></div>

        {/* Other Games */}
        <OtherGames currentGameId="pips" />

        {/* Win Modal */}
        {gameState === "won" && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="text-center mb-6">
                <div className="text-green-600 font-bold text-2xl mb-3">Congratulations! Puzzle Complete!</div>
                <div className="text-gray-600 text-lg mb-1">Time: {formatTime(currentTime)}</div>
                <div className="text-gray-500 text-sm">
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} difficulty
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() =>
                    downloadCertificate(
                      {
                        gameName: "Pips",
                        playerName: playerName,
                        stats: [
                          `Difficulty: ${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}`,
                          `Time: ${formatTime(currentTime)}`,
                          `Date: ${getDayOfWeek()}`,
                        ],
                        subtitle: "Domino Puzzle Master!",
                        backgroundColor: { start: "#8b5cf6", end: "#6366f1" },
                      },
                      `pips-certificate-${playerName}-${difficulty}-${Date.now()}.png`
                    )
                  }
                  className="bg-green-100 hover:bg-green-200 text-green-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
                >
                  Download Certificate
                </button>
                <button
                  onClick={() => {
                    setGameStarted(false);
                    setGameState("playing");
                    setPlacedDominoes([]);
                    setDominoRotations({});
                    setSelectedDominoIndex(null);
                    setCurrentTime(0);
                  }}
                  className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
                >
                  Play Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* How to Play Modal */}
        {showHowToPlay && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowHowToPlay(false)}
          >
            <div
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sm:p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">How to Play</h2>
                <button
                  onClick={() => setShowHowToPlay(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center"
                >
                  ×
                </button>
              </div>

              <p className="text-lg text-gray-700 mb-6 font-medium">
                Fill the entire board with all the dominoes to meet the conditions.
              </p>

              <div className="space-y-4 text-gray-700">
                <div className="flex gap-3">
                  <span className="text-indigo-600 font-bold text-lg">•</span>
                  <p>
                    <strong>Tap to rotate</strong> dominoes before placing them on the board.
                  </p>
                </div>

                <div className="flex gap-3">
                  <span className="text-indigo-600 font-bold text-lg">•</span>
                  <p>
                    <strong>Tap</strong> dominoes onto the board to fill all spaces.
                  </p>
                </div>

                <div className="flex gap-3">
                  <span className="text-indigo-600 font-bold text-lg">•</span>
                  <p>
                    <strong>Click placed dominoes</strong> and then the domino tray to return them.
                  </p>
                </div>

                <div className="flex gap-3">
                  <span className="text-indigo-600 font-bold text-lg">•</span>
                  <div>
                    <p className="mb-2">
                      <strong>Colored regions</strong> have conditions attached to their bottom right that must be
                      fulfilled:
                    </p>
                    <ul className="ml-4 space-y-2">
                      <li>
                        <strong>Number (2)</strong>: Dots must add up to this number
                      </li>
                      <li>
                        <strong>Equal (=)</strong>: Dots in this region must all be the same number
                      </li>
                      <li>
                        <strong>Not Equal (≠)</strong>: Dots in this region must all be different numbers
                      </li>
                      <li>
                        <strong>&lt;8</strong>: Dots must sum to less than this number
                      </li>
                      <li>
                        <strong>&gt;2</strong>: Dots must sum to more than this number
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <span className="text-indigo-600 font-bold text-lg">•</span>
                  <p>
                    <strong>Complete the puzzle</strong> by placing all dominoes so that every region's condition is
                    satisfied.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowHowToPlay(false)}
                className="w-full mt-6 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 rounded-xl font-semibold transition-all"
              >
                Got it!
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PipsPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      <PipsPageContent />
    </Suspense>
  );
}
